import type { ReactElement, ReactNode } from 'react';
import { splitParagraphs } from '../format';

export function EmberWritingProse({
  title,
  text,
  draft,
  settling = false,
  logState,
  logConversationId,
  children,
  underTitle,
}: {
  title: string;
  text: string;
  draft: boolean;
  settling?: boolean;
  /** ui.seen `ember_writing` state (draft | final); the seen is emitted only when set. */
  logState?: string;
  logConversationId?: string;
  children?: ReactNode;
  /** §4.17: the seals row, between the title and the prose. */
  underTitle?: ReactNode;
}): ReactElement {
  const paragraphs = splitParagraphs(text);
  const penInTitle = draft && Boolean(title) && paragraphs.length === 0;
  return (
    <div
      className={`result-writing${draft ? ' is-draft' : ' is-final'}${settling ? ' is-settling' : ''}`}
      data-log-seen={logState ? 'ember_writing' : undefined}
      data-log-state={logState}
      data-log-conversation-id={logState ? logConversationId : undefined}
    >
      <header className="result-header">
        <p className="result-section-label">
          Your Ember
          {(draft || settling) && (
            <span className={`result-draft-badge${settling ? ' is-leaving' : ''}`}>Draft</span>
          )}
        </p>
        {title ? (
          <h1 className="result-title" dir="auto">
            {title}
            {penInTitle && <WritingPen />}
          </h1>
        ) : (
          <div className="result-draft-title-line" aria-hidden />
        )}
        {underTitle}
        <div className="result-text" aria-live={draft ? 'polite' : 'off'}>
          {paragraphs.map((paragraph, index) => (
            <p className="result-para" dir="auto" key={index}>
              {paragraph.text}
              {draft && index === paragraphs.length - 1 && <WritingPen />}
            </p>
          ))}
          {draft && paragraphs.length === 0 && (
            <div className="result-draft-lines" aria-label="Ember is writing">
              <span><WritingPen /></span><span /><span />
            </div>
          )}
        </div>
        {children}
      </header>
    </div>
  );
}

function WritingPen(): ReactElement {
  return (
    <span className="result-writing-pen" aria-hidden>
      <svg viewBox="0 0 34 34" focusable="false">
        <path className="result-writing-pen-body" d="M8.5 24.8 23.8 9.5l4.7 4.7-15.3 15.3-6.1 1.4z" />
        <path className="result-writing-pen-wood" d="m7.1 30.9 1.4-6.1 4.7 4.7z" />
        <path className="result-writing-pen-mark" d="m7.1 30.9 2.2-.5-1.7-1.7z" />
        <path className="result-writing-pen-band" d="m21.6 11.7 4.7 4.7" />
      </svg>
    </span>
  );
}
